import { Transition } from "@headlessui/react"
import classNames from "classnames"
import React, { useContext } from "react"
import { useMutation, useQuery, useQueryClient } from "react-query"

import { statusToken } from "src/components/Badge/status_tokens"
import ActionConfirmModal from "src/components/DataTableV2/ActionConfirmModal"
import Toggle from "src/components/Toggle"
import MaybeRestrictedTooltip from "src/components/Tooltip/MaybeRestrictedTooltip"

import {
  queryActiveIntegration,
  updateApideckConsumer,
} from "../../api/Apideck"
import Badge from "../../components/Badge"
import Button from "../../components/Button"
import Loader from "../../components/Loader"
import { useToast } from "../../hooks/use_toast"
import { AccountingContext } from "./AccountingView"
import ApideckModalManager from "./ApideckModalManager"
import IntegrationConfiguration from "./IntegrationConfiguration"
import useAccountsAndItemsImport from "./useAccountsAndItemsImport"

const ACTIVE = "active"
const INACTIVE = "inactive"

const SetupTab = () => {
  const {
    marinaSlug,
    connectionGuideUrl,
    integration: currentIntegration,
    setIntegration,
    accountingV2Enabled,
    userHasAdminRole,
  } = useContext(AccountingContext)

  const showToast = useToast()
  const queryClient = useQueryClient()

  const { data: integration } = useQuery({
    queryKey: ["integration", marinaSlug],
    queryFn: () => {
      return queryActiveIntegration({ marinaSlug })
    },
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (integration) => {
      return integration?.preparing && 2000
    },
    initialData: currentIntegration,
  })

  const isActiveIntegration = integration?.status === ACTIVE
  const isSetupComplete = integration?.setupComplete || false

  const {
    hasImportedCodes,
    isImportingCodes,
    manualCodesConfirmation,
    onImportClick,
    syncStatus,
  } = useAccountsAndItemsImport({ source: "setup" })

  const {
    mutate: updateIntegrationStatus,
    isLoading: isUpdatingIntegrationStatus,
  } = useMutation({
    mutationFn: (mutationData) => {
      const status = isActiveIntegration ? INACTIVE : ACTIVE
      const data = userHasAdminRole ? { status } : {}

      return updateApideckConsumer({ marinaSlug, data })
    },
    onMutate: async (mutationData) => {
      await queryClient.cancelQueries({ queryKey: ["integration", marinaSlug] })

      const optimisticIntegration = {
        ...integration,
        status: isActiveIntegration ? INACTIVE : ACTIVE,
      }

      queryClient.setQueryData(
        ["integration", marinaSlug],
        optimisticIntegration
      )
      return { previousIntegration: integration }
    },
    onSuccess: (updatedIntegration) => {
      setIntegration(updatedIntegration)
      showToast(`Integration status successfully updated!`, {
        type: "success",
        duration: 4,
      })
    },
    onError: (error, mutationData, context) => {
      console.log(error)
      queryClient.setQueryData(
        ["integration", marinaSlug],
        context.previousIntegration
      )
      showToast(
        `Error updating integration. Please try again or contact support@mayday.com for assistance.`,
        {
          type: "error",
          duration: 6,
        }
      )
    },
  })

  const syncButtonText = isImportingCodes
    ? "Importing..."
    : hasImportedCodes
    ? accountingV2Enabled
      ? "Resync Accounts and Items"
      : "Resync Accounts"
    : accountingV2Enabled
    ? "Import Accounts and Items"
    : "Import Accounts"

  const renderStatusToggle = () => {
    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-2">
          <h4 className="font-semibold">Integration Status</h4>
          <Badge
            color={statusToken(isActiveIntegration ? "On" : "Off")}
            text={isActiveIntegration ? "On" : "Off"}
            variant="small"
          />
        </div>
        {isActiveIntegration
          ? "Syncing is currently enabled. Select “off” below to temporarily pause syncing."
          : "Syncing is currently paused. Enable when you’re ready to begin syncing."}
        <div className="mt-2 w-32">
          <MaybeRestrictedTooltip
            restricted={!userHasAdminRole}
            placement="right"
            text={"Must have an admin role to update integration status."}
          >
            <div className="flex items-center gap-2">
              <span
                className={classNames("font-semibold", {
                  "text-muted": isActiveIntegration,
                })}
              >
                OFF
              </span>
              <div className="pt-2">
                <Toggle
                  checked={isActiveIntegration}
                  onChange={updateIntegrationStatus}
                  name="integration-status-toggle"
                  disabled={!userHasAdminRole || isUpdatingIntegrationStatus}
                />
              </div>
              <span
                className={classNames("font-semibold", {
                  "text-muted": !isActiveIntegration,
                })}
              >
                ON
              </span>
            </div>
          </MaybeRestrictedTooltip>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="max-w-screen-lg py-5 text-gray-900">
        <h4 className="font-semibold">Connect to Accounting System</h4>
        <span className="text-gray-800">
          Dockwa enables you to integrate your Dockwa financial data with your
          accounting system to make your revenue accounting process more
          accurate and streamlined. For more information on how to get setup,
          take a look at our <a href={connectionGuideUrl}>connection guide</a>.
        </span>
        <div className="my-5">
          <ApideckModalManager integration={integration} />
        </div>
        {accountingV2Enabled && isSetupComplete ? renderStatusToggle() : null}
        <Transition
          show={isSetupComplete}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          {integration?.preparing ? (
            <Loader name="your integration data..." />
          ) : (
            <div className="mt-10">
              {!accountingV2Enabled && (
                <>
                  <h4 className="font-semibold">Select Accounting Basis</h4>
                  <span className="text-gray-800">
                    Accrual Accounting records revenue and expenses when
                    transactions occur but before money is received or dispensed
                    while Cash Accounting records revenue and expenses when cash
                    related to those transactions actually is received or
                    dispensed.
                  </span>
                </>
              )}
              <IntegrationConfiguration integration={integration} />
              <h4 className="mt-8 font-semibold">
                {accountingV2Enabled
                  ? "Import Accounts and Items"
                  : "Import Chart of Accounts"}
              </h4>
              <span className="text-gray-800">
                {accountingV2Enabled ? (
                  <>
                    Once connected, import your Chart of Accounts and Items into
                    Dockwa. This will also attempt to automatically map your
                    products/items from your accounting system to your items in
                    Dockwa. This mapping will be attempted both by item SKU and
                    item name. You can manually map any items that don&apos;t
                    get auto-mapped in the Item Mapping tab above.
                  </>
                ) : (
                  <>
                    Once connected, import your Accounting System accounts and
                    then go to the manage tab to select which accounts to use
                    for mapping. If connecting for the first time, all existing
                    accounts in Dockwa will be overwritten.
                  </>
                )}
              </span>
              <div className="my-5 flex items-center gap-2">
                <Button
                  onClick={onImportClick}
                  disabled={isImportingCodes}
                  variant="primary"
                  isLoading={isImportingCodes}
                >
                  {syncButtonText}
                </Button>
                <Badge
                  color={statusToken(syncStatus.status)}
                  text={syncStatus.status}
                />
              </div>
            </div>
          )}
        </Transition>
      </div>
      <ActionConfirmModal {...manualCodesConfirmation} />
    </div>
  )
}

export default SetupTab
